import React, {useEffect, useMemo, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import Select from 'react-select';
import Datepicker from '../../components/Datepicker';
import {
  useClearInspectionsMutation,
  useGetEquipmentTypeQuery,
  useGetLogSheetsPeriodsQuery,
  useGetUsersByAgencyQuery,
  useLazyGetFacilityQuery,
  useLazyGetSheetsQuery,
  useRemoveLogSheetMutation,
  useSetManualDateMutation,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import moment from 'moment';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {useNavigate} from 'react-router-dom';
import CustomPaginate from '../../components/PaginateComponent';
import {Helmet} from 'react-helmet';
import {useDebounce, useFocus} from './hooks';
import {toast} from 'react-toastify';
import LogSheetsRow from '../../components/LogSheetsRow';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';

export default function LogSheets() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const urlParams = new URLSearchParams(window.location.search);
  const [currentPage, setCurrentPage] = useState(1);
  const [disableSearch, setDisableSearch] = useState(false);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersOptions, setUsersOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [manualReason, setManualReason] = useState('');
  const [manualError, setManualError] = useState({date: false, reason: false});
  const [subscribtionDates, setSubscribtionDates] = useState({
    start_date: null,
  });
  const [adjustsDates, setAdjustsDates] = useState(false);
  const statusOptions = useMemo(
    () => [
      {
        value: 'new',
        label: t('logSheets.statusOptions.new'),
      },
      {
        value: 'half',
        label: t('logSheets.statusOptions.partiallyCompleted'),
      },
      {
        value: 'done',
        label: t('logSheets.statusOptions.completed'),
      },
    ],
    [t],
  );

  const defaultOption = useMemo(
    () =>
      urlParams.get('status')
        ? statusOptions?.filter(
            item => item?.value == urlParams.get('status'),
          )[0]
        : null,
    [statusOptions],
  );

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [periodOptions, setPeriodOptions] = useState([]);
  const sheetsFilterSearch = localStorage.getItem('sheetsFilter-search');
  const [search, setSearch] = useState(
    sheetsFilterSearch ? JSON.parse(sheetsFilterSearch) : '',
  );
  const [showBtn, setShowBtn] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const isInitialMountRef = useRef(true);

  const sheetsFilterDateFrom = localStorage.getItem('sheetsFilter-date_from');
  const sheetsFilterDateTo = localStorage.getItem('sheetsFilter-date_to');
  const sheetsFilterEquipment = localStorage.getItem(
    'sheetsFilter-equipment_type_id',
  );
  const sheetsFilterUser = localStorage.getItem('sheetsFilter-user_id');
  const sheetsFilterFacility = localStorage.getItem('sheetsFilter-facility_id');
  const sheetsFilterStatus = localStorage.getItem('sheetsFilter-status');
  const sheetsFilterPeriod = localStorage.getItem('sheetsFilter-period');

  const [getSheets, {data, isFetching, isSuccess, isError, error}] =
    useLazyGetSheetsQuery();
  const {
    data: equipmentTypeList,
    error: equipmentTypeError,
    isLoading: equipmentTypeLoading,
  } = useGetEquipmentTypeQuery();

  const {
    data: usersList,
    error: usersError,
    isLoading: usersLoading,
  } = useGetUsersByAgencyQuery();

  const [
    getFacility,
    {data: facilityList, error: facilityError, isLoading: facilityLoading},
  ] = useLazyGetFacilityQuery();

  const {
    data: periodsList,
    error: periodsError,
    isLoading: periodsLoading,
    isSuccess: periodsIsSuccess,
  } = useGetLogSheetsPeriodsQuery();

  const [
    removeInspections,
    {
      data: clearInspectionData,
      isFetching: clearInspectionIsLoading,
      isSuccess: clearInspectionIsSuccess,
      isError: clearInspectionIsError,
      error: clearInspectionError,
    },
  ] = useClearInspectionsMutation();
  const [setManualDate] = useSetManualDateMutation();

  const [
    removeLogSheet,
    {
      data: removeLogSheetData,
      isFetching: removeLogSheetIsLoading,
      isSuccess: removeLogSheetIsSuccess,
      isError: removeLogSheetIsError,
      error: removeLogSheetError,
    },
  ] = useRemoveLogSheetMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    error,
    clearInspectionError,
    removeLogSheetIsError,
  ]);
  const debouncedSearchValue = useDebounce(search, 500);

  useEffect(() => {
    if (confirmModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [confirmModal]);

  useEffect(() => {
    getFacility();
    // Don't call getSheets here - let the filter effect handle it after filters are initialized
  }, []);
  useEffect(() => {
    setFacilityOptions(
      facilityList?.map(item => ({value: item.id, label: item.name})),
    );
  }, [facilityList]);

  useEffect(() => {
    if (usersList) {
      let s = usersList?.map(item => {
        return {value: item?.id, label: item.name};
      });
      setUsersOptions(s);
    }
  }, [usersList]);

  useEffect(() => {
    // Only set default option if there's a status in URL params and filters haven't been initialized
    if (!filtersInitialized && !sheetsFilterStatus && defaultOption) {
      setSelectedOption(defaultOption);
    }
  }, [defaultOption, filtersInitialized]);

  useEffect(() => {
    if (periodsList) {
      let s = Object.values(periodsList).map(item => ({
        value: item.key,
        label: item.label,
      }));
      setPeriodOptions(s);
    }
  }, [periodsList]);

  useEffect(() => {
    if (equipmentTypeList) {
      let s = equipmentTypeList?.map(item => {
        return {value: item?.id, label: item?.title};
      });
      setEquipmentTypeOptions(s);
    }
  }, [equipmentTypeList]);

  useEffect(() => {
    if (
      equipmentTypeLoading ||
      isFetching ||
      usersLoading ||
      facilityLoading ||
      clearInspectionIsLoading ||
      removeLogSheetIsLoading
    ) {
      setDisableSearch(true);
      dispatch(setLoader(true));
    } else {
      setDisableSearch(false);
      dispatch(setLoader(false));
    }
  }, [
    isFetching,
    isSuccess,
    equipmentTypeLoading,
    usersLoading,
    facilityLoading,
    clearInspectionIsLoading,
    removeLogSheetIsLoading,
  ]);

  useEffect(() => {
    // Only restore filters once when all options are available
    if (filtersInitialized) return;

    const settled = (saved, list, err, options) => {
      if (!saved) return true;
      if (err) return true;
      if (list === undefined) return false;
      if (list.length === 0) return true;
      return (options?.length ?? 0) > 0;
    };
    if (
      !settled(
        sheetsFilterEquipment,
        equipmentTypeList,
        equipmentTypeError,
        equipmentTypeOptions,
      ) ||
      !settled(sheetsFilterUser, usersList, usersError, usersOptions) ||
      !settled(
        sheetsFilterFacility,
        facilityList,
        facilityError,
        facilityOptions,
      ) ||
      !settled(
        sheetsFilterPeriod,
        periodsList && Object.values(periodsList),
        periodsError,
        periodOptions,
      )
    ) {
      return;
    }

    // Restore filters from localStorage if they exist
    if (sheetsFilterFacility && facilityOptions.length > 0) {
      setSelectedFacility(
        facilityOptions?.filter(item => item?.value == sheetsFilterFacility)[0],
      );
    }
    if (sheetsFilterUser && usersOptions.length > 0) {
      setSelectedUser(
        usersOptions?.filter(item => item?.value == sheetsFilterUser)[0],
      );
    }
    if (sheetsFilterEquipment && equipmentTypeOptions.length > 0) {
      setSelectedEquipment(
        equipmentTypeOptions?.filter(
          item => item?.value == sheetsFilterEquipment,
        )[0],
      );
    }
    if (sheetsFilterPeriod && periodOptions.length > 0) {
      const periodValue = JSON.parse(sheetsFilterPeriod);
      setSelectedPeriod(
        periodOptions?.filter(item => item?.value == periodValue)[0],
      );
    }
    if (sheetsFilterStatus && statusOptions.length > 0) {
      const statusValue = JSON.parse(sheetsFilterStatus);
      setSelectedOption(
        statusOptions?.filter(item => item?.value == statusValue)[0],
      );
    }
    if (
      sheetsFilterDateFrom &&
      moment(JSON.parse(sheetsFilterDateFrom)).format('YYYY-MM-DD') !==
        'Invalid date'
    ) {
      setDateFrom(
        moment(JSON.parse(sheetsFilterDateFrom)).format('YYYY-MM-DD'),
      );
    } else {
      setDateFrom(null);
    }
    if (
      sheetsFilterDateTo &&
      moment(JSON.parse(sheetsFilterDateTo)).format('YYYY-MM-DD') !==
        'Invalid date'
    ) {
      setDateTo(moment(JSON.parse(sheetsFilterDateTo)).format('YYYY-MM-DD'));
    } else {
      setDateTo(null);
    }

    // Mark filters as initialized after restoring (or if no filters to restore)
    setFiltersInitialized(true);
  }, [
    facilityOptions,
    usersOptions,
    equipmentTypeOptions,
    periodOptions,
    statusOptions,
    filtersInitialized,
    equipmentTypeList,
    usersList,
    facilityList,
    periodsList,
    equipmentTypeError,
    usersError,
    facilityError,
    periodsError,
  ]);

  useEffect(() => {
    // Wait for filters to be initialized from localStorage before making any API calls
    if (!filtersInitialized) {
      return;
    }

    // Make initial API call after filters are initialized
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      getSheets({
        page: 1,
        search: debouncedSearchValue,
        equipment_type_id: selectedEquipment?.value
          ? selectedEquipment?.value
          : null,
        user_id: selectedUser?.value ? selectedUser?.value : null,
        date_from: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null,
        date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : null,
        facility_id: selectedFacility?.value ? selectedFacility?.value : null,
        status: selectedOption?.value ? selectedOption?.value : null,
        period: selectedPeriod?.value ? selectedPeriod?.value : null,
      });
      return;
    }

    // Reset to page 1 when filters change (after initial load)
    getSheets({
      page: 1,
      search: debouncedSearchValue,
      equipment_type_id: selectedEquipment?.value
        ? selectedEquipment?.value
        : null,
      user_id: selectedUser?.value ? selectedUser?.value : null,
      date_from: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null,
      date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : null,
      facility_id: selectedFacility?.value ? selectedFacility?.value : null,
      status: selectedOption?.value ? selectedOption?.value : null,
      period: selectedPeriod?.value ? selectedPeriod?.value : null,
    });

    setCurrentPage(1);
  }, [
    filtersInitialized,
    selectedEquipment,
    selectedUser,
    dateFrom,
    dateTo,
    selectedFacility,
    selectedOption,
    selectedPeriod,
    debouncedSearchValue,
  ]);

  useEffect(() => {
    setShowBtn(() => {
      if (
        search ||
        dateFrom ||
        dateTo ||
        selectedEquipment ||
        selectedFacility ||
        selectedPeriod ||
        selectedUser
      ) {
        return true;
      } else {
        return false;
      }
    });
  }, [
    search,
    dateFrom,
    dateTo,
    selectedEquipment,
    selectedFacility,
    selectedPeriod,
    selectedUser,
  ]);

  useLoader([isFetching]);

  const handleSearch = e => {
    setSearch(e.target.value);
    saveFilters('search', e.target.value);
    setCurrentPage(1);
  };

  const handlePageClick = page => {
    setCurrentPage(page?.selected + 1);
    getSheets({
      page: page?.selected + 1,
      search: debouncedSearchValue,
      equipment_type_id: selectedEquipment?.value
        ? selectedEquipment?.value
        : null,
      user_id: selectedUser?.value ? selectedUser?.value : null,
      date_from: dateFrom ? moment(dateFrom)?.format('YYYY-MM-DD') : null,
      date_to: dateTo ? moment(dateTo)?.format('YYYY-MM-DD') : null,
      facility_id: selectedFacility?.value ? selectedFacility?.value : null,
      status: selectedOption?.value ? selectedOption?.value : null,
      period: selectedPeriod?.value ? selectedPeriod?.value : null,
    });
    window.scrollTo(0, 0);
  };

  const removeInspection = () => {
    removeInspections({
      page: currentPage,
      search: debouncedSearchValue,
      equipment_type_id: selectedEquipment?.value
        ? selectedEquipment?.value
        : null,
      user_id: selectedUser?.value ? selectedUser?.value : null,
      date_from: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null,
      date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : null,
      facility_id: selectedFacility?.value ? selectedFacility?.value : null,
      status: selectedOption?.value ? selectedOption?.value : null,
      period: selectedPeriod?.value ? selectedPeriod?.value : null,
    }).then(res => {
      toast.success(t('logSheets.messages.updateSuccess'));
      setConfirmModal(false);
      navigate('/log-sheets');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    });
  };

  const renderConfirmModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p></p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => {
                setConfirmModal(false);
              }}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('logSheets.confirmModal.title')}
          </div>
          <div className='modal__content-bottom'>
            <button
              className='close'
              onClick={() => {
                setConfirmModal(false);
              }}
            >
              {t('logSheets.confirmModal.close')}
            </button>
            <button className='save danger' onClick={removeInspection}>
              {t('logSheets.confirmModal.yes')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const handleSetManualDate = id => {
    setManualDate({
      id: id,
      body: {
        manual_inspection_date: subscribtionDates.start_date,
        description: manualReason,
        update_equipment_and_inspection_dates: adjustsDates,
      },
    }).then(res => {
      toast.success(t('logSheets.messages.updateSuccess'));
      setManualReason('');
      setSubscribtionDates({
        start_date: null,
      });
      setAdjustsDates(false);
      getSheets({
        page: currentPage,
        search: debouncedSearchValue,
        equipment_type_id: selectedEquipment?.value
          ? selectedEquipment?.value
          : null,
        user_id: selectedUser?.value ? selectedUser?.value : null,
        date_from: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null,
        date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : null,
        facility_id: selectedFacility?.value ? selectedFacility?.value : null,
        status: selectedOption?.value ? selectedOption?.value : null,
        period: selectedPeriod?.value ? selectedPeriod?.value : null,
      });
    });
  };

  const removeLogElement = id => {
    removeLogSheet({id: id}).then(() => {
      toast.success(t('logSheets.messages.deleteSuccess'));
      getSheets({
        page: currentPage,
        search: debouncedSearchValue,
        equipment_type_id: selectedEquipment?.value
          ? selectedEquipment?.value
          : null,
        user_id: selectedUser?.value ? selectedUser?.value : null,
        date_from: dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null,
        date_to: dateTo ? moment(dateTo).format('YYYY-MM-DD') : null,
        facility_id: selectedFacility?.value ? selectedFacility?.value : null,
        status: selectedOption?.value ? selectedOption?.value : null,
        period: selectedPeriod?.value ? selectedPeriod?.value : null,
      });
    });
  };

  const saveFilters = (name, data) => {
    if (data) {
      localStorage.setItem(`sheetsFilter-${name}`, JSON.stringify(data));
    } else {
      localStorage.removeItem(`sheetsFilter-${name}`);
    }
  };

  const clearAllFilters = () => {
    setSelectedEquipment(null);
    setSelectedUser(null);
    setSelectedFacility(null);
    setSelectedPeriod(null);
    setSelectedOption(null);
    setDateFrom(null);
    setDateTo(null);
    setSearch('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    localStorage.removeItem('sheetsFilter-date_from');
    localStorage.removeItem('sheetsFilter-date_to');
    localStorage.removeItem('sheetsFilter-equipment_type_id');
    localStorage.removeItem('sheetsFilter-user_id');
    localStorage.removeItem('sheetsFilter-facility_id');
    localStorage.removeItem('sheetsFilter-status');
    localStorage.removeItem('sheetsFilter-period');
    localStorage.removeItem('sheetsFilter-search');
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('logSheets.pageTitle')}</title>
      </Helmet>
      {confirmModal && renderConfirmModal()}
      <DashboardHeader
        title={t('logSheets.title')}
        type='logSheets'
        action={() => setConfirmModal(true)}
        showBtn={showBtn && userInfo?.role?.name === 'global'}
      />
      <div className='sheets'>
        <div className='sheets__header'>
          <Input
            image={'/assets/icons/search.svg'}
            placeholder={t('logSheets.searchPlaceholder')}
            maxwidth={200}
            onChange={handleSearch}
            defaultValue={search}
            innerRef={inputRef}
          />
          <Datepicker
            keys={dateFrom}
            type={'from'}
            width={160}
            setValue={e => {
              setDateFrom(e);
              saveFilters('date_from', moment(e).format('YYYY-MM-DD'));
            }}
            value={dateFrom}
          />
          <Datepicker
            keys={dateTo}
            type={'to'}
            width={160}
            setValue={e => {
              setDateTo(e);
              saveFilters('date_to', moment(e).format('YYYY-MM-DD'));
            }}
            value={dateTo}
          />
          <Select
            placeholder={t('logSheets.filters.equipmentType')}
            options={equipmentTypeOptions}
            isClearable
            value={selectedEquipment}
            key={JSON.stringify(selectedEquipment) + '1'}
            onChange={selected => {
              if (selected === null) {
                setSelectedEquipment(null);
                saveFilters('equipment_type_id', null);
                localStorage.removeItem('sheetsFilter-equipment_type_id');
              } else {
                setSelectedEquipment(selected);
                saveFilters('equipment_type_id', selected.value);
              }
            }}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
          <Select
            placeholder={t('logSheets.filters.submittedBy')}
            options={usersOptions}
            isClearable
            value={selectedUser}
            onChange={selected => {
              if (selected === null) {
                setSelectedUser(null);
                saveFilters('user_id', null);
                localStorage.removeItem('sheetsFilter-user_id');
              } else {
                setSelectedUser(selected);
                saveFilters('user_id', selected.value);
              }
            }}
            key={usersOptions}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 200,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
          <Select
            placeholder={t('logSheets.filters.facility')}
            options={facilityOptions}
            isClearable
            value={selectedFacility}
            onChange={selected => {
              if (selected === null) {
                setSelectedFacility(null);
                saveFilters('facility_id', null);
                localStorage.removeItem('sheetsFilter-facility_id');
              } else {
                setSelectedFacility(selected);
                saveFilters('facility_id', selected.value);
              }
            }}
            key={JSON.stringify(facilityOptions)}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 150,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
          <Select
            placeholder={t('logSheets.filters.status')}
            options={statusOptions}
            isClearable
            onChange={selected => {
              if (selected === null) {
                setSelectedOption(null);
                saveFilters('status', null);
                localStorage.removeItem('sheetsFilter-status');
              } else {
                setSelectedOption(selected);
                saveFilters('status', selected.value);
              }
            }}
            value={selectedOption}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 200,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
          <Select
            noOptionsMessage={() => t('logSheets.filters.noOptions')}
            placeholder={t('logSheets.filters.period')}
            options={periodOptions?.filter(el => el.value !== 'bi_weekly')}
            isClearable
            value={selectedPeriod}
            onChange={selected => {
              if (selected === null) {
                setSelectedPeriod(null);
                saveFilters('period', null);
                localStorage.removeItem('sheetsFilter-period');
              } else {
                setSelectedPeriod(selected);
                saveFilters('period', selected.value);
              }
            }}
            key={JSON.stringify(facilityOptions) + '2'}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                borderRadius: 8,
                width: 150,
                height: 42,
                backgroundColor: '#F4F5F6',
              }),
            }}
          />
          <div className='sheets__header-clear' onClick={clearAllFilters}>
            {t('logSheets.buttons.clearAll')}
          </div>
        </div>
        <div className='sheets__box'>
          <table className='sheets__table'>
            <thead className='sheets__table-head'>
              <tr>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.name')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.inspectionDate')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.completedDate')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.equipment')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.facility')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.nfc')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.period')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.completed')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.completedBy')}
                </th>
                <th className='sheets__table-th'>
                  {t('logSheets.tableHeaders.comments')}
                </th>
              </tr>
            </thead>
            <tbody className='sheets__table-body'>
              {data?.data?.length ? (
                data?.data?.map(sheet => (
                  <LogSheetsRow
                    sheet={sheet}
                    key={sheet.id}
                    removeLogElement={removeLogElement}
                    handleSetManualDate={handleSetManualDate}
                    manualReason={manualReason}
                    setManualReason={setManualReason}
                    subscribtionDates={subscribtionDates}
                    setSubscribtionDates={setSubscribtionDates}
                    manualError={manualError}
                    setManualError={setManualError}
                    adjustsDates={adjustsDates}
                    setAdjustsDates={setAdjustsDates}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={10}>
                    <div className='emptyList'>
                      <img src='/assets/empty-list.png' alt='empty' />
                      <p>{t('logSheets.messages.noData')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <CustomPaginate
            pageCount={data?.pages}
            handlePageClick={handlePageClick}
            currentPage={currentPage}
          />
        </div>
      </div>
    </Wrapper>
  );
}
